import { Button, Kind } from '@thanx/uikit/button'
import { Popover } from '@thanx/uikit/popover'
import { Text } from '@thanx/uikit/text'
import { defaultTheme, useStyletron } from '@thanx/uikit/theme'
import ConfirmModal from 'components/ConfirmModal'
import InfoBox from 'components/InfoBox'
import PopoverMenu, { PopoverOption } from 'components/PopoverMenu'
import Section from 'components/Section'
import { buildTranslate } from 'locales'
import React from 'react'
import Markdown, { Components } from 'react-markdown'
import CmsPage from 'scenes/Cms/components/CmsPage'

const t = buildTranslate('cms.checkout_message')

const linkTooltipOverrides = {
  Inner: {
    style: {
      padding: '8px 12px',
      backgroundColor: defaultTheme.colors.white,
      borderRadius: '4px',
      boxShadow: '0px 4px 8px rgba(54, 66, 78, 0.3)',
    },
  },
}

// Wraps markdown links with a hover tooltip that shows the URL.
const markdownComponents: Components = {
  a: ({ href, children }) => (
    <Popover
      triggerType="hover"
      placement="right"
      showArrow={false}
      content={
        <p className="font-size-12 grey-70 mb-0" style={{ lineHeight: '16px' }}>
          {href}
        </p>
      }
      overrides={linkTooltipOverrides}
    >
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    </Popover>
  ),
}

type Props = {
  existingMessage: string
  canEdit: boolean
  isLoading: boolean
  isRemoveModalOpen: boolean
  onEdit: () => void
  onRemove: () => void
  onOpenRemoveModal: () => void
  onCloseRemoveModal: () => void
  showTitle?: boolean
}

const ViewingCard: React.FC<Props> = ({
  existingMessage,
  canEdit,
  isLoading,
  isRemoveModalOpen,
  onEdit,
  onRemove,
  onOpenRemoveModal,
  onCloseRemoveModal,
  showTitle = true,
}) => {
  const [css] = useStyletron()
  return (
    <CmsPage
      showTitle={showTitle}
      title={t('title')}
      description={t('description')}
      isLoading={isLoading}
    >
      <InfoBox type="tip" content={t('add_description')} className="mb-m" />
      <Section className="p-m">
        <div className="d-flex align-items-center justify-content-between mb-m">
          <Text.Header4 className="my-0">{t('message_heading')}</Text.Header4>
          {canEdit && (
            <div className="d-flex align-items-center gap-m">
              <PopoverMenu dismissOnClickOutside closeOnClick>
                <PopoverOption onClick={onOpenRemoveModal}>
                  {t('remove')}
                </PopoverOption>
              </PopoverMenu>
              <Button kind={Kind.MINIMAL} onClick={onEdit}>
                {t('edit')}
              </Button>
            </div>
          )}
        </div>
        <div>
          <p className="bold font-size-14 mb-xs">{t('message_label')}</p>
          <div
            className={`grey-70 mt-0 ${css({ overflowWrap: 'anywhere' })}`}
            data-testid="checkout-message-content"
          >
            <Markdown components={markdownComponents}>
              {existingMessage}
            </Markdown>
          </div>
        </div>
      </Section>
      <ConfirmModal
        title={t('remove_modal.title')}
        description={t('remove_modal.description')}
        confirmText={t('remove_modal.confirm')}
        isOpen={isRemoveModalOpen}
        onClose={onCloseRemoveModal}
        onConfirm={() => {
          onCloseRemoveModal()
          onRemove()
        }}
      />
    </CmsPage>
  )
}

export default ViewingCard
